import { EntityEventType } from '@racemap/sdk/schema/entityEvents';
import { useListEntityEvents } from '.';

/**
 * Custom hook to fetch the latest configuration update event for a tracker.
 *
 * Internally uses `useListEntityEvents` to retrieve configuration events (e.g., start, confirmation, refusal).
 * Returns only the most recent event (limit: 1).
 *
 * @param {string} trackerId - Tracker ID.
 * @returns {Object} Contains:
 * @property {EntityEvent | null} configUpdateResult - Latest configuration update event or `null`.
 * @property {boolean} isLoading - A boolean indicating if the event is currently being loaded.
 * @property {Error | undefined} error - An error object if an error occurred during the fetch operation.
 */

export const useConfigurationUpdateResult = (trackerId: string) => {
  const { events, isLoading, error } = useListEntityEvents({
    entityIds: [trackerId],
    eventTypes: [
      EntityEventType.CONFIG_UPDATE_COMMAND_STARTING,
      EntityEventType.CONFIG_UPDATE_COMMAND_CONFIRMED,
      EntityEventType.CONFIG_UPDATE_COMMAND_REFUSED,
      EntityEventType.CONFIG_UPDATE_PROCESS_REFUSED_LOW_BATTERY,
      EntityEventType.CONFIG_DOWNLOAD_STARTING,
      EntityEventType.CONFIG_DOWNLOAD_SUCCESS,
      EntityEventType.CONFIG_DOWNLOAD_FAILED,
      EntityEventType.CONFIG_DEVICE_UPDATE_STARTING,
      EntityEventType.CONFIG_DEVICE_UPDATE_SUCCESS,
      EntityEventType.CONFIG_DEVICE_UPDATE_FAILED,
    ],
    limit: 1,
  });

  return {
    configUpdateResult: events && events.length > 0 ? events[0] : null,
    isLoading,
    error,
  };
};
